import { DocumentOcrConfig, parseDate, icaoToIso2, mapGender } from './document-ocr';

// ===== Diploma (factory for diplomas 1-6) =====

function createDiplomaConfig(n: number): DocumentOcrConfig {
  return {
    documentType: `diploma${n}`,
    expectedDocumentType: 'un diplôme ou certificat scolaire',
    prompt: `Extract the following fields from this diploma or degree certificate image.
Read all visible text on the document. The document may have multiple pages.

Return ONLY a JSON object with these exact keys (use null for any field you cannot read):
{
  "diplomaName": "Name/type of the degree or diploma (e.g. Baccalauréat, Master, Licence, DEC)",
  "field": "Field of study or specialization",
  "school": "Full name of the educational institution",
  "location": "City and country of the institution (e.g. Paris, France)",
  "startDate": "YYYY-MM-DD format (start of studies, if visible)",
  "endDate": "YYYY-MM-DD format (graduation or completion date)"
}

Return ONLY the JSON, no markdown, no explanation.`,
    fieldMapping: {
      diplomaName: { wizardFieldId: `diploma${n}Name`, displayLabel: 'Nom du diplôme' },
      field: { wizardFieldId: `diploma${n}Field`, displayLabel: 'Domaine d\'études' },
      school: { wizardFieldId: `diploma${n}School`, displayLabel: 'Établissement' },
      location: { wizardFieldId: `diploma${n}Location`, displayLabel: 'Lieu' },
      startDate: { wizardFieldId: `diploma${n}StartDate`, displayLabel: 'Date de début', transform: parseDate },
      endDate: { wizardFieldId: `diploma${n}EndDate`, displayLabel: 'Date de fin', transform: parseDate },
    },
  };
}

// ===== National ID =====

const nationalIdConfig: DocumentOcrConfig = {
  documentType: 'nationalId',
  expectedDocumentType: 'une carte nationale d\'identité',
  prompt: `Extract the following fields from this national identity card image.
Read both sides if visible.

Return ONLY a JSON object with these exact keys (use null for any field you cannot read):
{
  "idNumber": "The national identity card number",
  "issuingCountry": "3-letter ICAO country code of the issuing country (e.g. FRA, MAR, DZA, TUN)",
  "validity": "Validity period as text, e.g. '01/01/2020 - 01/01/2030'"
}

Return ONLY the JSON, no markdown, no explanation.`,
  fieldMapping: {
    idNumber: { wizardFieldId: 'nationalIdNumber', displayLabel: 'Numéro de la pièce' },
    issuingCountry: { wizardFieldId: 'nationalIdCountry', displayLabel: 'Pays de délivrance', transform: icaoToIso2 },
    validity: { wizardFieldId: 'nationalIdValidity', displayLabel: 'Dates de validité' },
  },
};

// ===== Marriage Certificate =====

const marriageCertificateConfig: DocumentOcrConfig = {
  documentType: 'marriageCertificate',
  expectedDocumentType: 'un acte de mariage ou certificat d\'union civile',
  prompt: `Extract the following fields from this marriage certificate or civil union document.
The document may be in French, English, Arabic, or another language.

Return ONLY a JSON object with these exact keys (use null for any field you cannot read):
{
  "marriageDate": "YYYY-MM-DD format (date of the marriage or civil union)",
  "spouseFullName": "Full name of the spouse (the other person on the certificate)"
}

Return ONLY the JSON, no markdown, no explanation.`,
  fieldMapping: {
    marriageDate: { wizardFieldId: 'marriageDate', displayLabel: 'Date du mariage', transform: parseDate },
    spouseFullName: { wizardFieldId: 'spouseFullName', displayLabel: 'Nom du conjoint(e)' },
  },
};

// ===== Spouse Passport =====

const spousePassportConfig: DocumentOcrConfig = {
  documentType: 'spousePassport',
  expectedDocumentType: 'un passeport',
  skipNameVerification: true,
  prompt: `Extract the following fields from this passport image (this is the applicant's spouse's passport).
Read the visual text fields on the passport page, not just the MRZ zone.

Return ONLY a JSON object with these exact keys (use null for any field you cannot read):
{
  "fullName": "Full name (surname followed by given names, properly capitalized)",
  "gender": "M or F",
  "dateOfBirth": "YYYY-MM-DD format"
}

Return ONLY the JSON, no markdown, no explanation.`,
  fieldMapping: {
    fullName: { wizardFieldId: 'spouseFullName', displayLabel: 'Nom du conjoint(e)' },
    gender: { wizardFieldId: 'spouseGender', displayLabel: 'Sexe du conjoint(e)', transform: mapGender },
    dateOfBirth: { wizardFieldId: 'spouseDateOfBirth', displayLabel: 'Date de naissance du conjoint(e)', transform: parseDate },
  },
};

// ===== Divorce Document =====

const divorceDocumentConfig: DocumentOcrConfig = {
  documentType: 'divorceDocument',
  expectedDocumentType: 'un jugement de divorce ou acte de dissolution d\'union',
  skipNameVerification: true,
  prompt: `Extract the following fields from this divorce decree or dissolution of union document.
The document may have multiple pages.

Return ONLY a JSON object with these exact keys (use null for any field you cannot read):
{
  "exSpouseName": "Full name of the ex-spouse",
  "exSpouseDob": "YYYY-MM-DD format (date of birth of the ex-spouse, if visible)",
  "unionPeriod": "The period of the union as text, e.g. '06/2010 - 03/2018'"
}

Return ONLY the JSON, no markdown, no explanation.`,
  fieldMapping: {
    exSpouseName: { wizardFieldId: 'exSpouseName', displayLabel: 'Nom de l\'ex-conjoint(e)' },
    exSpouseDob: { wizardFieldId: 'exSpouseDob', displayLabel: 'Date de naissance de l\'ex-conjoint(e)', transform: parseDate },
    unionPeriod: { wizardFieldId: 'exSpouseUnionPeriod', displayLabel: 'Période de l\'union' },
  },
};

// ===== Employment Attestation =====

const empAttestationConfig: DocumentOcrConfig = {
  documentType: 'empAttestation',
  expectedDocumentType: 'une attestation d\'emploi ou lettre d\'employeur',
  prompt: `Extract the following fields from this employment attestation or employment letter.
Read all visible text including letterhead and body.

Return ONLY a JSON object with these exact keys (use null for any field you cannot read):
{
  "jobTitle": "The employee's job title or position",
  "startDate": "YYYY-MM-DD format (employment start date)",
  "companyName": "Name of the employer or company",
  "companyAddress": "City and country of the company"
}

Return ONLY the JSON, no markdown, no explanation.`,
  fieldMapping: {
    jobTitle: { wizardFieldId: 'empJobTitle', displayLabel: 'Titre de poste' },
    startDate: { wizardFieldId: 'empStartDate', displayLabel: 'Date de début', transform: parseDate },
    companyName: { wizardFieldId: 'empCompanyName', displayLabel: 'Nom de l\'entreprise' },
    companyAddress: { wizardFieldId: 'empCompanyAddress', displayLabel: 'Adresse de l\'entreprise' },
  },
};

// ===== Passport =====

const passportConfig: DocumentOcrConfig = {
  documentType: 'passport',
  expectedDocumentType: 'un passeport',
  prompt: `Extract the following fields from this passport image. Read the visual text fields on the passport page, not just the MRZ zone.

Return ONLY a JSON object with these exact keys (use null for any field you cannot read):
{
  "firstName": "Given names (properly capitalized)",
  "lastName": "Surname / family name (properly capitalized)",
  "dateOfBirth": "YYYY-MM-DD format",
  "gender": "M or F",
  "nationality": "3-letter ICAO country code (e.g. FRA, CAN, MAR)",
  "passportNumber": "Passport document number",
  "passportExpiryDate": "YYYY-MM-DD format",
  "passportIssueDate": "YYYY-MM-DD format (date of issue / date de délivrance)",
  "issuingCountry": "3-letter ICAO country code of the issuing country",
  "birthCity": "City/place of birth as written on the passport",
  "birthCountry": "3-letter ICAO country code of the country of birth"
}

Return ONLY the JSON, no markdown, no explanation.`,
  fieldMapping: {
    firstName: { wizardFieldId: 'firstName', displayLabel: 'Prénom' },
    lastName: { wizardFieldId: 'lastName', displayLabel: 'Nom de famille' },
    dateOfBirth: { wizardFieldId: 'dateOfBirth', displayLabel: 'Date de naissance', transform: parseDate },
    gender: { wizardFieldId: 'gender', displayLabel: 'Sexe', transform: mapGender },
    nationality: { wizardFieldId: 'nationality', displayLabel: 'Nationalité', transform: icaoToIso2 },
    passportNumber: { wizardFieldId: 'passportNumber', displayLabel: 'Numéro de passeport' },
    passportExpiryDate: { wizardFieldId: 'passportExpiryDate', displayLabel: "Date d'expiration", transform: parseDate },
    passportIssueDate: { wizardFieldId: 'passportIssueDate', displayLabel: 'Date de délivrance', transform: parseDate },
    issuingCountry: { wizardFieldId: 'passportCountry', displayLabel: 'Pays de délivrance', transform: icaoToIso2 },
    birthCity: { wizardFieldId: 'birthCity', displayLabel: 'Ville de naissance' },
    birthCountry: { wizardFieldId: 'birthCountry', displayLabel: 'Pays de naissance', transform: icaoToIso2 },
  },
};

// ===== Export lookup map =====

export const DOCUMENT_OCR_CONFIGS: Record<string, DocumentOcrConfig> = {
  passportDocument: passportConfig,
  nationalIdDocument: nationalIdConfig,
  marriageCertificate: marriageCertificateConfig,
  spousePassport: spousePassportConfig,
  divorceDocument: divorceDocumentConfig,
  empAttestationCurrent: empAttestationConfig,
  diploma1Document: createDiplomaConfig(1),
  diploma2Document: createDiplomaConfig(2),
  diploma3Document: createDiplomaConfig(3),
  diploma4Document: createDiplomaConfig(4),
  diploma5Document: createDiplomaConfig(5),
  diploma6Document: createDiplomaConfig(6),
};
